import { getBomProductPicks, type BomProductPick } from '@api/bom-management';
import {
    getProductionOrderDocumentUrl,
    openProductionOrderPreview,
    listProductionOrders,
    listAllWorkOrders,
    listVendorsForPicker,
    listWarehousesForPicker,
    type VendorPickItem,
    type WarehousePickItem,
    type WorkOrder,
    type WorkOrderStatus,
} from '@api/work-order';
import PageHeader from '@components/common/PageHeader';
import { ColumnPicker, type ColumnPickerDef } from '@components/ui/ColumnPicker';
import {
    DataSheet,
    type DataSheetColumn,
    type DataSheetFilterConfig,
} from '@components/ui/DataSheet';
import { DateRangePicker, type UiDateRange } from '@components/ui/DateRange';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
// Aliased: the local StatusPill renders work-order statuses; this one renders an arbitrary
// label with a tone, which is what "Not sent" needs.
import TonePill from '@components/ui/StatusPill';
import useToaster from '@hooks/toaster/useToaster';
import usePersistentState from '@hooks/usePersistentState';
import { Button, Group, Text, Tooltip } from '@mantine/core';
import { PlusIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { hasDayRange, inDayRange, NO_DAY_RANGE } from '@utils/productionWindow';

import { formatMoney } from '@components/containers/procurementOrders/shared';

import NewWorkOrderModal, { type NewWorkOrderPrefill } from './NewWorkOrderModal';
import {
    buildProductionOrderRows,
    editedSinceSent,
    resolveCancelAction,
    resolveEditAction,
    toSkuProductionOrderRows,
    type ProductionOrderGridRow,
} from './productionOrderRows';
import SendProductionOrderModal from './SendProductionOrderModal';
import StatusPill from './StatusPill';
import { useCancelProduction } from './useCancelProduction';
import { formatCount } from '@utils/currency';

const STATUS_OPTIONS: { value: string; label: string }[] = [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ISSUED', label: 'Issued' },
    { value: 'IN_PRODUCTION', label: 'In Production' },
    { value: 'READY_TO_SHIP', label: 'Ready to Ship' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'RECEIVED', label: 'Received' },
    { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Draft',
    ISSUED: 'Issued',
    IN_PRODUCTION: 'In Production',
    READY_TO_SHIP: 'Ready to Ship',
    SHIPPED: 'Shipped',
    RECEIVED: 'Received',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
};

/** Nothing picked: every manufacturer, every status, every product. */
const EMPTY_FILTERS: FilterValue = { manufacturer: [], status: [], product: [] };

type WorkOrderRow = WorkOrder & {
    productName: string;
    productionLocationName: string;
    /** The order's code and date, looked up from the orders the page already holds. */
    orderCode: string | null;
    orderDate: string | null;
};

type Tab = 'orders' | 'lines';

/**
 * Two views of the same commitment.
 *
 * The page used to be line-grained only, which left an order with no row of its own — nowhere
 * to show its status, whether it had been sent, or the document it was sent as. Those are facts
 * about the ORDER, and they need an order to sit on.
 */
const TABS = [
    { id: 'orders', label: 'Production orders' },
    { id: 'lines', label: 'SKU lines' },
];

/**
 * The orders-sheet columns an operator may hide, keyed as `orderColumns` keys them. The
 * order code and the row actions are not here: a row with no name is not a row, and an
 * action nobody can reach is not an action.
 */
const ORDER_COLUMNS_ON_BY_DEFAULT: ColumnPickerDef[] = [
    { key: 'subject', label: 'Manufacturer / SKU' },
    { key: 'orderDate', label: 'Ordered on' },
    { key: 'dueDate', label: 'Complete by' },
    { key: 'status', label: 'Order / line status' },
    { key: 'skuCount', label: 'SKUs' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'total', label: 'Order value' },
    { key: 'qcPending', label: 'QC pending' },
    { key: 'qcApproved', label: 'QC approved' },
    { key: 'dispatched', label: 'Shipped' },
    { key: 'openQuantity', label: 'Open quantity' },
    { key: 'orderId', label: 'Document' },
];

const ORDER_COLUMNS_OFF_BY_DEFAULT: ColumnPickerDef[] = [{ key: 'sentTo', label: 'Sent' }];

const WorkOrdersContainer: FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    /**
     * One set of picks for both tabs. A manufacturer or status chosen on the orders tab still
     * holds when the reader flips to the lines, which is how they drill from one to the other.
     */
    const [filters, setFilters] = useState<FilterValue>(EMPTY_FILTERS);
    /** Orders PLACED in this window, on both tabs; a line takes its order's date. */
    const [range, setRange] = useState<UiDateRange>(NO_DAY_RANGE);
    const [modalOpen, setModalOpen] = useState(false);
    const [prefill, setPrefill] = useState<NewWorkOrderPrefill | null>(null);
    const [tab, setTab] = useState<Tab>('orders');
    const { showToastError } = useToaster();

    // URL handoff from "Create WO" CTAs elsewhere (e.g. FG Calculator at-risk row).
    useEffect(() => {
        const prefillProductId = searchParams.get('prefillProductId');
        const prefillQuantity = searchParams.get('prefillQuantity');
        const prefillDueDate = searchParams.get('prefillDueDate');
        if (!prefillProductId || !prefillQuantity || !prefillDueDate) return;

        setPrefill({
            lines: [
                { productId: prefillProductId, quantity: Number(prefillQuantity) || 0 },
            ],
            dueDate: prefillDueDate,
        });
        setModalOpen(true);

        // Clear params so a manual refresh doesn't re-fire the prefill.
        const next = new URLSearchParams(searchParams);
        next.delete('prefillProductId');
        next.delete('prefillQuantity');
        next.delete('prefillDueDate');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // The whole book rather than a page of it. Search and the filters run in the browser, as
    // they do on the orders tab, and a brand's production book is tens of lines, not thousands.
    const workOrdersQuery = useQuery({
        queryKey: ['work-orders', 'all'],
        queryFn: listAllWorkOrders,
    });

    const ordersQuery = useQuery({
        queryKey: ['production-orders'],
        queryFn: listProductionOrders,
    });

    // Lookups to render IDs as names. Small datasets, rarely change mid-session.
    const productsQuery = useQuery<BomProductPick[]>({
        queryKey: ['bom-product-picks'],
        queryFn: getBomProductPicks,
    });

    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['wo-warehouses'],
        queryFn: listWarehousesForPicker,
    });

    const vendorsQuery = useQuery<VendorPickItem[]>({
        queryKey: ['wo-vendors'],
        queryFn: listVendorsForPicker,
    });

    const rows: WorkOrderRow[] = useMemo(() => {
        const orders = workOrdersQuery.data ?? [];
        // The code the manufacturer sees, off the orders already fetched for the other tab.
        const orderById = new Map(
            (ordersQuery.data?.orders ?? []).map((order) => [order.id, order]),
        );
        const productNameById = new Map(
            (productsQuery.data ?? []).map((p) => [p.productId ?? '', p.name ?? '']),
        );
        const warehouseNameById = new Map(
            (warehousesQuery.data ?? []).map((w) => [w.warehouseId, w.name]),
        );
        const vendorNameById = new Map(
            (vendorsQuery.data ?? []).map((v) => [v.vendorId, v.name]),
        );
        return orders.map((wo) => ({
            ...wo,
            productName: productNameById.get(wo.productId) || wo.productId,
            productionLocationName:
                wo.productionLocationType === 'WAREHOUSE'
                    ? (warehouseNameById.get(wo.productionLocationId) ?? wo.productionLocationId)
                    : (vendorNameById.get(wo.productionLocationId) ?? wo.productionLocationId),
            orderCode: orderById.get(wo.productionOrderId ?? '')?.code ?? null,
            orderDate: orderById.get(wo.productionOrderId ?? '')?.orderDate ?? null,
        }));
    }, [
        workOrdersQuery.data,
        ordersQuery.data,
        productsQuery.data,
        warehousesQuery.data,
        vendorsQuery.data,
    ]);

    // ─── The order-grained view ─────────────────────────────────────────────

    const orderRows = useMemo(
        () => buildProductionOrderRows(ordersQuery.data?.orders ?? []),
        [ordersQuery.data],
    );

    /**
     * Whether the sheet is grouped by product rather than by order.
     *
     * SAME CONTROL, SAME DEFAULT as the two site-level production grids. This is the brand's
     * whole book rather than one site's, so it is the only place a product gathers orders
     * placed with DIFFERENT manufacturers — but the way it is switched on should not differ.
     */
    const [ordersGroupBy, setOrdersGroupBy] = usePersistentState<string | null>(
        'reparo.productionOrders.groupBy',
        null,
    );
    const isSkuGrouped = ordersGroupBy === 'sku';

    /**
     * Which columns the orders sheet shows — the same control the Inventory DoH view offers,
     * remembered the same way as the grouping above.
     *
     * SENT SHIPS HIDDEN. It lists every address an order went to, which on a ten-recipient
     * order is a paragraph tall and makes the row it sits in three times the height of its
     * neighbours. The answer it carries — did this go out, and does it owe a resend — is still
     * one click away in the picker for whoever is chasing it.
     */
    const [hiddenOrderCols, setHiddenOrderCols] = usePersistentState<string[]>(
        'reparo.productionOrders.hiddenCols',
        ORDER_COLUMNS_OFF_BY_DEFAULT.map((def) => def.key),
    );
    const hiddenOrderColSet = useMemo(() => new Set(hiddenOrderCols), [hiddenOrderCols]);

    /**
     * The picks, read the same way at both levels: an order and a line each name their
     * manufacturer by id and carry the order's date.
     */
    const matchesPicks = useCallback(
        (row: { productionLocationId: string; status: string; orderDate: string | null }) => {
            const makers = filters.manufacturer ?? [];
            const statuses = filters.status ?? [];
            return (
                (makers.length === 0 || makers.includes(row.productionLocationId)) &&
                (statuses.length === 0 || statuses.includes(row.status)) &&
                inDayRange(row.orderDate, range)
            );
        },
        [filters, range],
    );

    const products = useMemo(() => filters.product ?? [], [filters.product]);
    const wantsProduct = useCallback(
        (productId: string) => products.length === 0 || products.includes(productId),
        [products],
    );

    /*
     * Narrowed BEFORE the SKU fold, so a product row only gathers the orders that passed: a
     * count of "3 orders" must be three the reader asked about. A product pick keeps an order
     * that has that SKU on it, whole; grouped by SKU it then keeps only that SKU's group.
     */
    const keptOrderRows = useMemo(
        () =>
            orderRows.filter(
                (row) =>
                    matchesPicks(row) &&
                    (products.length === 0 ||
                        (row.lines ?? []).some((line) => wantsProduct(line.productId))),
            ),
        [orderRows, matchesPicks, products, wantsProduct],
    );
    const displayedOrderRows = useMemo(
        () =>
            isSkuGrouped
                ? toSkuProductionOrderRows(keptOrderRows).filter((row) => wantsProduct(row.productId))
                : keptOrderRows,
        [keptOrderRows, isSkuGrouped, wantsProduct],
    );

    const displayedRows = useMemo(
        () => rows.filter((row) => matchesPicks(row) && wantsProduct(row.productId)),
        [rows, matchesPicks, wantsProduct],
    );

    const countLabel = (shown: number, total: number, noun: string) =>
        `${shown}${shown === total ? '' : ` of ${total}`} ${noun}`;

    const filterConfig = useMemo<DataSheetFilterConfig>(() => {
        // Only who actually holds an order with this brand, named as the grid names them.
        // Only who actually holds an order with this brand, and only what has been ordered, each
        // named as the grid names them. Both tabs' rows feed in, so the list is whole whichever
        // tab loaded first.
        const makers = new Map<string, string>();
        const skus = new Map<string, string>();
        for (const row of orderRows) {
            makers.set(row.productionLocationId, row.subject);
            for (const line of row.lines ?? []) skus.set(line.productId, line.productName);
        }
        for (const row of rows) {
            makers.set(row.productionLocationId, row.productionLocationName);
            skus.set(row.productId, row.productName);
        }
        const asOptions = (byId: Map<string, string>) =>
            [...byId]
                .map(([value, label]) => ({ value, label }))
                .sort((a, b) => a.label.localeCompare(b.label));
        return {
            label: 'Filter',
            params: [
                {
                    key: 'manufacturer',
                    label: 'Manufacturer',
                    mode: 'multi',
                    options: asOptions(makers),
                    searchPlaceholder: 'Search manufacturers...',
                },
                { key: 'status', label: 'Status', mode: 'multi', options: STATUS_OPTIONS },
                {
                    key: 'product',
                    label: 'Product',
                    mode: 'multi',
                    options: asOptions(skus),
                    searchPlaceholder: 'Search products...',
                },
            ],
            value: filters,
            onChange: (value) => setFilters({ ...EMPTY_FILTERS, ...value }),
        };
    }, [orderRows, rows, filters]);

    const dateControl = (
        <DateRangePicker
            value={range}
            onChange={setRange}
            onReset={hasDayRange(range) ? () => setRange(NO_DAY_RANGE) : undefined}
            title="Orders placed in this range"
        />
    );

    /** Kept by id so the modal reads live data after a resend rather than a stale row. */
    const [sendingOrderId, setSendingOrderId] = useState<string | null>(null);
    /** The draft being corrected in place; opens the authoring modal on it. */
    const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
    const sendingOrder =
        ordersQuery.data?.orders.find((order) => order.id === sendingOrderId) ?? null;

    const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null);

    /**
     * Fetch the signed link, then hand it to the browser.
     *
     * Two steps rather than an href because the link is minted per request and expires — an
     * anchor would need either a permanently public object or a URL that goes stale in place.
     */
    const openDocument = async (orderId: string, kind: 'issued' | 'preview' = 'issued') => {
        setOpeningDocumentId(orderId);
        try {
            if (kind === 'preview') {
                await openProductionOrderPreview(orderId);
            } else {
                const { url } = await getProductionOrderDocumentUrl(orderId);
                window.open(url, '_blank', 'noopener');
            }
        } catch (err: any) {
            showToastError({
                title: 'Could not open the order document',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setOpeningDocumentId(null);
        }
    };

    // Both grids re-read after a cancel: the order tab for the header, the line tab for the line.
    const { requestCancel, cancellingKey } = useCancelProduction(() =>
        Promise.all([ordersQuery.refetch(), workOrdersQuery.refetch()]),
    );

    const orderColumns: DataSheetColumn<ProductionOrderGridRow>[] = [
        {
            key: 'code',
            /*
             * ONE COLUMN, TWO LEVELS, which is what this grid already does with the column
             * beside it. Grouped by SKU the product heads the group and the order codes sit
             * under it; grouped by order it is the order code throughout.
             */
            header: isSkuGrouped ? 'Product / Order' : 'Order',
            width: 160,
            sticky: true,
        },
        {
            key: 'subject',
            /*
             * The manufacturer at BOTH levels once grouped by SKU — named when a product is
             * made in one place, counted when it is made in several. That is the answer this
             * grouping exists to give: how much of a thing is outstanding, and with whom.
             */
            header: isSkuGrouped ? 'Manufacturer' : 'Manufacturer / SKU',
            width: 240,
        },
        {
            /*
             * PLACED, not due. Sits before "Complete by" so the two read as a span: raised on
             * the 13th, wanted by the 31st. Repeated on each line, so a line read or exported
             * on its own still says when.
             */
            key: 'orderDate',
            header: 'Ordered on',
            width: 120,
            render: (value: string | null) => value ?? '',
        },
        { key: 'dueDate', header: 'Complete by', width: 120 },
        {
            /*
             * Labelled by LEVEL, not just "Status". An order row shows the order's status and a
             * line row shows the line's, and they differ legitimately — an order is ISSUED while
             * one SKU is cancelled. Calling both "Status" is what made an issued order read as a
             * draft on the SKU tab.
             */
            key: 'status',
            header: 'Order / line status',
            width: 150,
            filterable: true,
            render: (value: string) => <StatusPill status={value as WorkOrderStatus} />,
            csvValue: (value: string) => STATUS_LABEL[value] ?? value,
        },
        {
            key: 'skuCount',
            // How many SKUs an order holds, or how many orders want a SKU — the same count
            // read from whichever side the sheet is grouped.
            header: isSkuGrouped ? 'Orders' : 'SKUs',
            width: 80,
            align: 'right',
            render: (value: number | null) => (value === null ? '' : value),
        },
        {
            key: 'quantity',
            header: 'Quantity',
            width: 120,
            align: 'right',
            render: (value: number) => formatCount(value ?? 0),
        },
        {
            /*
             * WHAT IT IS WORTH, GST included — the figure the manufacturer will invoice.
             *
             * A DASH IS NOT A ZERO. An order raised before pricing existed carries no value at
             * all; one deliberately priced at nothing carries 0.00. Both are live, and coercing
             * the first into the second would quietly claim a price nobody set.
             *
             * Each row formats in its own currency rather than assuming one, because currency is
             * stored per order and a list may hold more than one.
             */
            key: 'total',
            header: 'Order value',
            width: 140,
            align: 'right',
            render: (value: string | null, row) =>
                value === null || value === undefined ? (
                    <Text fz={13} c={neutralColors.textMuted}>
                        —
                    </Text>
                ) : (
                    formatMoney(Number(value), row.currency ?? 'INR')
                ),
            // Sorted and filtered on the number, not the formatted string, or "1,000" would
            // rank between "1" and "2".
            valueAccessor: (row) => (row.total === null ? null : Number(row.total)),
            csvValue: (value: string | null) => value ?? '',
        },
        {
            key: 'qcPending',
            header: 'QC pending',
            width: 130,
            align: 'right',
            renderHeader: () => (
                <Tooltip label="Manufactured but not yet ruled on, and still at the manufacturer site. It can still be dispatched - the brand inspects the load on arrival." multiline w={280} withArrow>
                    <span>QC pending</span>
                </Tooltip>
            ),
            render: (value: number) =>
                value > 0 ? (
                    // Worth a look rather than a blocker: unruled goods may still travel.
                    <Text fz={13} c={semanticColors.warning.dark}>
                        {formatCount(value)}
                    </Text>
                ) : (
                    <Text fz={13} c={neutralColors.textMuted}>
                        0
                    </Text>
                ),
        },
        {
            /*
             * A STAGE, NOT A HISTORY. Cumulative "ever passed" would overlap Shipped
             * completely — a batch that passed and then went would sit in both, inviting a
             * reader to add them. The batches register keeps the cumulative reading, where On
             * hand tells the two apart. Its neighbour is netted the same way, and has to be:
             * unruled stock may now be dispatched, so it would otherwise double-count Shipped.
             *
             * Split by the same rule the site's screens use — one implementation in common — so
             * the brand's list and the manufacturer's cannot disagree about what has cleared.
             */
            key: 'qcApproved',
            header: 'QC approved',
            width: 140,
            align: 'right',
            renderHeader: () => (
                <Tooltip
                    label="Passed QC and still at the manufacturer site. Once it ships it moves to Sent."
                    multiline
                    w={260}
                    withArrow
                >
                    <span>QC approved</span>
                </Tooltip>
            ),
            render: (value: number) => formatCount(value ?? 0),
        },
        {
            /*
             * HOW MUCH OF IT HAS ACTUALLY ARRIVED. The order-grained view answered "what have we
             * commissioned" and stopped there, so the question it invited next — has any of it
             * come back yet — had to be answered somewhere else, per work order, by hand.
             *
             * Muted at zero: an order nothing has shipped against is the normal state of a
             * freshly issued one, not a problem to be picked out in bold.
             */
            key: 'dispatched',
            header: 'Shipped',
            width: 120,
            align: 'right',
            render: (value: number) =>
                value > 0 ? (
                    formatCount(value)
                ) : (
                    <Text fz={13} c={neutralColors.textMuted}>
                        0
                    </Text>
                ),
        },
        {
            /*
             * WHAT IS STILL OWED: ordered minus what has actually left, floored per line so an
             * over-shipped SKU cannot cancel out a short one.
             */
            key: 'openQuantity',
            header: 'Open quantity',
            width: 140,
            align: 'right',
            render: (value: number) =>
                value > 0 ? (
                    formatCount(value)
                ) : (
                    // Nothing owed is the finished state, not a figure to chase.
                    <Text fz={13} c={neutralColors.textMuted}>
                        0
                    </Text>
                ),
        },
        {
            /*
             * The durable answer to "did this go out". A toast lasts four seconds; this is
             * still here tomorrow, and names the address it actually reached.
             */
            key: 'sentTo',
            header: 'Sent',
            width: 230,
            render: (_value, row) => {
                if (row.kind !== 'order') return '';
                if (!row.sentAt) {
                    return <TonePill label="Not emailed" tone="warning" />;
                }
                return (
                    <Group gap={6} wrap="nowrap">
                        <Text fz={13} c={neutralColors.textSecondary}>
                            {new Date(row.sentAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                            })}
                            {row.sentTo ? ` · ${row.sentTo}` : ''}
                        </Text>
                        {/*
                          * The manufacturer holds an older version. An edit after the send
                          * regenerates the PDF but emails nobody, and this is what says a
                          * resend is owed - it clears the moment one goes out.
                          */}
                        {editedSinceSent(row) && (
                            <TonePill label="Edited since sent" tone="warning" />
                        )}
                    </Group>
                );
            },
            csvValue: (_value, row) => (row.sentAt ? `${row.sentAt} ${row.sentTo ?? ''}` : ''),
        },
        {
            key: 'orderId',
            header: 'Document',
            // Three xs buttons (Edit, Preview, Send) plus cell padding; 210 clipped "Preview".
            width: 240,
            render: (_value, row) => {
                /*
                 * GATED ON THE ID, not on the level. In the order grouping only an order row
                 * carries one, so this reads exactly as it did. Grouped by SKU the order row IS
                 * the line, and it should still open its own paperwork.
                 */
                if (!row.orderId) return '';
                const order = ordersQuery.data?.orders.find((o) => o.id === row.orderId);
                /*
                 * SEND STAYS AT THE ORDER. An order sent once is sent; offering the button on
                 * each of its SKU lines would be one act dressed as three.
                 */
                const sendable = row.kind === 'order'
                    && order?.productionLocationType === 'VENDOR'
                    && row.status !== 'CANCELLED';
                const editable = resolveEditAction(row).available;

                return (
                    <Group gap={8} wrap="nowrap">
                        {/*
                          * BEFORE SEND, because that is the order things happen in: an unsent
                          * draft is fixed, then sent, then — if it comes to that — cancelled.
                          * Gone the moment the order goes out; the server refuses edits then.
                          */}
                        {editable && (
                            <Button
                                size="xs"
                                variant={ButtonVariant.DEFAULT}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingOrderId(row.orderId);
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        {/*
                          * Nothing has been rendered for a draft, so this renders it now, stamped
                          * DRAFT and kept nowhere. Same gate as Send: an in-house run has no
                          * manufacturer to address.
                          */}
                        {!row.hasDocument && sendable && (
                            <Button
                                size="xs"
                                variant={ButtonVariant.DEFAULT}
                                loading={openingDocumentId === row.orderId}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void openDocument(row.orderId as string, 'preview');
                                }}
                            >
                                Preview
                            </Button>
                        )}
                        {row.hasDocument && (
                            <Button
                                size="xs"
                                variant={ButtonVariant.DEFAULT}
                                loading={openingDocumentId === row.orderId}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void openDocument(row.orderId as string);
                                }}
                            >
                                PDF
                            </Button>
                        )}
                        {sendable && (
                            <Button
                                size="xs"
                                variant={
                                    row.sentAt ? ButtonVariant.DEFAULT : ButtonVariant.ACTION
                                }
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setSendingOrderId(row.orderId);
                                }}
                            >
                                {row.sentAt ? 'Resend' : 'Send'}
                            </Button>
                        )}
                    </Group>
                );
            },
            csvValue: () => '',
        },
        {
            /*
             * ON THE ROW, at both levels. Cancelling used to live only on the line's detail
             * page, one tab and one click away from where anyone looks for it, and the order
             * itself could not be cancelled at all. Blank once there is nothing to cancel.
             */
            key: 'cancel',
            header: '',
            width: 130,
            align: 'right',
            render: (_value, row) => {
                const action = resolveCancelAction(row);
                const id = row.kind === 'order' ? row.orderId : row.workOrderId;
                if (!action.available || !id) return '';
                return (
                    <Button
                        size="xs"
                        variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                        loading={cancellingKey === row.key}
                        onClick={(event) => {
                            event.stopPropagation();
                            requestCancel({ key: row.key, kind: row.kind, code: row.code, id });
                        }}
                    >
                        {action.label}
                    </Button>
                );
            },
            csvValue: () => '',
        },
    ];

    const columns: DataSheetColumn<WorkOrderRow>[] = [
        { key: 'code', header: 'Line', width: 140, sticky: true },
        { key: 'productName', header: 'Product', width: 240 },
        {
            // The code the manufacturer sees, not a slice of the order's uuid. A dash on a work
            // order raised on its own, which is a line of no order.
            key: 'orderCode',
            header: 'Production order',
            width: 150,
            render: (value: string | null) => value ?? '—',
        },
        {
            key: 'orderDate',
            header: 'Ordered on',
            width: 110,
            render: (value: string | null) => value ?? '',
        },
        {
            key: 'createdAt',
            header: 'Created',
            width: 110,
            render: (value) =>
                value ? new Date(String(value)).toISOString().slice(0, 10) : '',
        },
        { key: 'dueDate', header: 'Due', width: 110 },
        {
            key: 'productionLocationName',
            header: 'Production location',
            width: 200,
            render: (value, row) =>
                `${value}${row.productionLocationType === 'VENDOR' ? ' (vendor)' : ''}`,
        },
        {
            key: 'status',
            header: 'Status',
            width: 140,
            render: (value) => <StatusPill status={String(value)} />,
            csvValue: (value: string) => STATUS_LABEL[value] ?? value,
        },
        {
            key: 'quantity',
            header: 'Ordered Quantity',
            width: 150,
            align: 'right',
            render: (value) => formatCount(value),
        },
        {
            key: 'receivedQuantity',
            header: 'Received Quantity',
            width: 150,
            align: 'right',
            render: (value) => formatCount(value ?? 0),
        },
        {
            // Same action the detail page offers, without the click to get there. The row
            // still opens on click; the button stops that so a cancel is never also a visit.
            key: 'cancel',
            header: '',
            width: 120,
            align: 'right',
            render: (_value, row) => {
                const action = resolveCancelAction({ kind: 'line', status: row.status });
                if (!action.available) return '';
                return (
                    <Button
                        size="xs"
                        variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                        loading={cancellingKey === row.id}
                        onClick={(event) => {
                            event.stopPropagation();
                            requestCancel({ key: row.id, kind: 'line', code: row.code, id: row.id });
                        }}
                    >
                        {action.label}
                    </Button>
                );
            },
            csvValue: () => '',
        },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <Group justify="space-between" align="flex-start">
                <PageHeader
                    icon="🏭"
                    title="Production Orders"
                    subtitle="What each manufacturer has been asked to make, SKU by SKU"
                />
                <Button
                    variant={ButtonVariant.ACTION}
                    leftSection={<PlusIcon size={16} />}
                    onClick={() => setModalOpen(true)}
                >
                    New Production Order
                </Button>
            </Group>

            {/*
              * ORDERS FIRST, because that is what the page is called and what a person opening
              * it is asking about. The SKU-line view stays: it answers a different question —
              * "what is being made" rather than "what did we commission" — and some work is
              * genuinely line-by-line.
              */}
            {tab === 'orders' ? (
                <DataSheet<ProductionOrderGridRow>
                    data={displayedOrderRows}
                    columns={orderColumns.filter((column) => !hiddenOrderColSet.has(column.key))}
                    tabs={TABS}
                    activeTab="orders"
                    onTabChange={(next) => setTab(next as Tab)}
                    title="Production orders"
                    subtitle={
                        ordersQuery.isLoading
                            ? 'Loading…'
                            : isSkuGrouped
                              ? `${displayedOrderRows.length} SKU(s) across ${countLabel(keptOrderRows.length, orderRows.length, 'order(s)')}`
                              : `${countLabel(keptOrderRows.length, orderRows.length, 'order(s)')}, newest first`
                    }
                    isLoading={ordersQuery.isLoading}
                    enableSearch
                    searchPlaceholder="Search by order, manufacturer or SKU..."
                    filters={[filterConfig]}
                    toolbarControls={dateControl}
                    // Expanding an order shows the SKUs it is made of; grouped by SKU, the
                    // orders that want it. Same component, same expansion.
                    getSubRows={(row) => row.lines}
                    /* The grid renders the control; the fold is ours, above. */
                    groupByOptions={[{ label: 'SKU', value: 'sku' }]}
                    activeGroupBy={ordersGroupBy}
                    onGroupByChange={setOrdersGroupBy}
                    toolbarActionsRight={
                        <ColumnPicker
                            defaultOnDefs={ORDER_COLUMNS_ON_BY_DEFAULT}
                            defaultOffDefs={ORDER_COLUMNS_OFF_BY_DEFAULT}
                            hiddenKeys={hiddenOrderCols}
                            onChange={setHiddenOrderCols}
                        />
                    }
                    getRowKey={(row) => row.key}
                    enableDownload
                    downloadFilename="production-orders.csv"
                    maxHeight="auto"
                />
            ) : (
                <DataSheet<WorkOrderRow>
                    data={displayedRows}
                    columns={columns}
                    tabs={TABS}
                    activeTab="lines"
                    onTabChange={(next) => setTab(next as Tab)}
                    title="SKU lines"
                    subtitle={
                        workOrdersQuery.data
                            ? `${countLabel(displayedRows.length, rows.length, 'SKU line(s)')} across production orders`
                            : ''
                    }
                    isLoading={workOrdersQuery.isLoading}
                    onRowClick={(row) => navigate(`/work-orders/${row.id}`)}
                    getRowKey={(row) => row.id}
                    enableSearch
                    searchPlaceholder="Search by line, SKU or order..."
                    filters={[filterConfig]}
                    toolbarControls={dateControl}
                    enableDownload
                    downloadFilename="production-order-lines.csv"
                />
            )}

            <SendProductionOrderModal
                order={sendingOrder}
                onClose={() => setSendingOrderId(null)}
                onSent={() => {
                    void ordersQuery.refetch();
                    void workOrdersQuery.refetch();
                }}
            />

            <NewWorkOrderModal
                opened={modalOpen || !!editingOrderId}
                prefill={prefill}
                editOrderId={editingOrderId}
                onClose={() => {
                    setModalOpen(false);
                    setPrefill(null);
                    setEditingOrderId(null);
                }}
            />
        </div>
    );
};

export default WorkOrdersContainer;
